import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router";
import { Card, Button } from "components/ui";
import { Page } from "components/shared/Page";
import "styles/mobile-responsive.css";
import { transformLeadDetailsApiData } from "utils/leadDataTransforms";
import { formatLeadName } from "utils/leadNameFormatter";

// Constants and Services
import { getLead, bulkUpdateLeadStage, getLeadJourneyIndicators } from "services/leadsService";
import LeadDetailsHeader from "components/leads/LeadDetails/LeadDetailsHeader";
import LeadBasicInfo from "components/leads/LeadDetails/LeadBasicInfo";
import StatusCards from "components/leads/LeadDetails/StatusCard";
import ProgressStepper from "components/leads/LeadDetails/ProgressStepper";
import SidebarNavigation from "components/leads/LeadDetails/SidebarNavigation";
import LeadRightSidebar from "components/leads/LeadDetails/LeadRightSidebar";
import LeadDetailsForm from "components/leads/LeadDetails/LeadForm";
import TimelineView from "components/leads/LeadDetails/TimeLineView";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { useConfirmModal } from "hooks/useConfirmModal";
import QuickLeadModal from "components/leads/LeadActions/QuickLeadModal";

// New Tab Components
import PaymentDetailsTab from "components/leads/LeadDetails/PaymentDetailsTab";
import AccountsApprovalTab from "components/leads/LeadDetails/AccountsApprovalTab";
import ApplicationFormViewTab from "components/leads/LeadDetails/ApplicationFormViewTab";
import NotesTab from "components/leads/LeadDetails/NotesTab";
import CalendarTab from "components/leads/LeadDetails/CalendarTab";
import CommunicationsTab from "components/leads/LeadDetails/CommunicationsTab";
import DocumentsTab from "components/leads/LeadDetails/DocumentsTab/DocumentsTab";
import TicketsTab from "components/leads/LeadDetails/TicketsTab";
import GoogleMeetTab from "components/leads/LeadDetails/GoogleMeetTab";
import OfferLetterTab from "components/leads/LeadDetails/OfferLetterTab";
import AcceptanceLetterTab from "components/leads/LeadDetails/AcceptanceLetterTab";
import { toast } from "sonner";
import StageChangeModal from "components/leads/LeadActions/StageChangeModal";
import AssignAcademicCounsellorModal from "components/leads/LeadActions/AssignAcademicCounsellorModal";
import AssignAccountantCounsellorModal from "components/leads/LeadActions/AssignAccountantCounsellorModal";


// Modal Components
import AssignOwnerModal from "components/leads/LeadActions/AssignOwnerModal";
import SetPriorityModal from "components/leads/LeadActions/SetPriorityModal";
import LeadAnalyticsModal from "components/leads/LeadActions/LeadAnalyticsModal";
import WhatsAppMessageModal from "components/leads/LeadActions/WhatsAppMessageModal";
import EmailMessageModal from "components/leads/LeadActions/EmailMessageModal";
import SMSMessageModal from "components/leads/LeadActions/SMSMessageModal";
import FollowUpScheduleModal from "components/leads/LeadActions/FollowUpScheduleModal";
import CallModal from "components/leads/LeadActions/CallModal";
import { createFollowUp, updateFollowUp, updateFollowUpStatus, getFollowUpEvents } from "services/followUpService";
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  UserIcon,
  ClockIcon,
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  Cog6ToothIcon
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Select, Textarea } from "components/ui/Form";
import { Badge } from "components/ui";
import SetInteractionLevelModal from "components/leads/LeadActions/SetInteractionLevelModal";
import { hasPermission } from "utils/permissionsUtils";
import Error401 from "../errors/401";

const LeadDetails = ({ leadIdProp = null, isModal = false, onClose = null, entityName = null, onLeadUpdate = null, onDataLoaded = null, initialSelectedTab = "leads-tab", initialSubTab = 0, scrollToSection = null, openNotesAddModal = false, setOpenNotesAddModal = null }) => {
  const { id: routeLeadId } = useParams();
  const contentRef = useRef(null);
  const finalEntityName = entityName || "Lead"; // Default to Lead if not provided
  const isInitialLoad = useRef(true);
  const params = useParams();

  const leadId = leadIdProp || params.id;
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
  const [selectedContentTab, setSelectedContentTab] = useState("lead-details");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedSubStage, setSelectedSubStage] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFlaged, setIsFlaged] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [progressData, setProgressData] = useState({});
  const [showQuickLeadModal, setShowQuickLeadModal] = useState(false);

  // Stage change confirmation modal
  const [stageConfirmModal, setStageConfirmModal] = useState({
    show: false,
    state: "pending",
    loading: false,
  });
  const [stageChangeData, setStageChangeData] = useState(null);

  // Modal states for all action modals
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInteractionLevelModal, setShowInteractionLevelModal] =
    useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showAcademicModal, setShowAcademicModal] = useState(false);
  const [showAccountantModal, setShowAccountantModal] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [selectedFollowUpForStatus, setSelectedFollowUpForStatus] = useState(null);
  const [statusFormData, setStatusFormData] = useState({
    status: '',
    statusId: null,
    outcome: '',
    outcomeId: null,
    eventTypeId: null,
    remarks: ''
  });
  const [followUpEvents, setFollowUpEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Internal state for notes modal (works in both page and modal mode)
  const [internalOpenNotesAddModal, setInternalOpenNotesAddModal] = useState(false);


  // Status options (same as FollowUpsCalendar)
  const statusOptions = [
    { id: 1, name: 'Pending' },
    { id: 2, name: 'Completed' },
    { id: 3, name: 'Missed' },
    { id: 4, name: 'Cancelled' },
    { id: 5, name: 'Failed' },
    { id: 6, name: 'Overdue' }
  ];

  // Final statuses that cannot be updated further
  const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Cancelled, Failed
  const FINAL_STATUS_NAMES = ['Completed', 'Missed', 'Cancelled', 'Failed'];

  // Helper to check if a follow-up status is final
  const isStatusFinal = (followUp) => {
    if (!followUp) return false;
    const statusId = followUp.StatusId || followUp.statusId;
    const statusName = followUp.StatusName || followUp.status;
    return FINAL_STATUS_IDS.includes(statusId) || FINAL_STATUS_NAMES.includes(statusName);
  };
  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load follow-up events when status modal opens
  useEffect(() => {
    if (showStatusModal) {
      loadFollowUpEvents();
    }
  }, [showStatusModal]);

  // Handle external note add modal trigger - switch to notes tab
  useEffect(() => {
    if (openNotesAddModal) {
      setSelectedTab('notes');
    }
  }, [openNotesAddModal]);

  const loadFollowUpEvents = async () => {
    setLoadingEvents(true);
    try {
      const result = await getFollowUpEvents();
      if (result.success) {
        setFollowUpEvents(result.data);
      } else {
        console.error("❌ Failed to load follow-up events:", result.error);
        setFollowUpEvents([]);
      }
    } catch (error) {
      console.error("💥 Error loading follow-up events:", error);
      setFollowUpEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Fetch lead data function - extracted for reuse
  const fetchLeadData = async (silent = false) => {
    if (!leadId) {
      setError("No lead ID provided");
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);

      const [result, journeyResult] = await Promise.all([
        getLead(leadId),
        getLeadJourneyIndicators(leadId)
      ]);

      if (journeyResult.success && journeyResult.data) {
        setProgressData(journeyResult.data);
      }

      if (result.success && result.data.data) {
        setApiResponse(result.data.data);

        // Transform API response to component-friendly format
        const transformedData = transformLeadDetailsApiData(
          result.data.data,
          leadId,
        );
        setLeadData(transformedData);
        setSelectedStage(transformedData.stage);
        setSelectedSubStage(transformedData.subStage);
        
        // Notify parent that data has been loaded
        if (onDataLoaded) {
          onDataLoaded(transformedData);
        }
      } else {
        // Fallback to dummy data if API fails
        console.warn("API failed, using dummy data:", result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error("Error fetching lead data:", err);
      setError(err.message);
      // Fallback to dummy data
    } finally {
      setLoading(false);
      // Notify parent about the update if this is a refresh (not initial load)
      if (!isInitialLoad.current && onLeadUpdate) {
        onLeadUpdate(leadId, 'update');
      }
      isInitialLoad.current = false;
    }
  };

  // Fetch lead data when component mounts or leadId changes
  useEffect(() => {
    fetchLeadData();
  }, [leadId]);

  // Update selected tab when initialSelectedTab changes (for deep linking)
  useEffect(() => {
    if (initialSelectedTab && initialSelectedTab !== selectedTab) {
      setSelectedTab(initialSelectedTab);
    }
  }, [initialSelectedTab]);

  // Auto-scroll to section and set correct sub-tab when scrollToSection is provided
  useEffect(() => {
    if (scrollToSection && leadData) {
      // If the scrollToSection is a sub-tab of the leads profile
      const isSubTab = [
        "lead-details",
        "offer-letter",
        "payment-details",
        "follow-up-calendar",
        "application-form",
        "google-meet",
      ].includes(scrollToSection);

      if (isSubTab) {
        setSelectedTab("leads-tab");
        setSelectedContentTab(scrollToSection);
      } else {
        // Handle main tabs
        const mainTabMapping = {
          'notes': 'notes',
          'documents': 'documents',
          'timeline': 'timeline',
          'communications': 'communications',
          'tickets': 'tickets'
        };
        if (mainTabMapping[scrollToSection]) {
          setSelectedTab(mainTabMapping[scrollToSection]);
        }
      }

      // Minimal delay (50ms) to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const element = document.getElementById(`section-${scrollToSection}`);
        if (element) {
          element.scrollIntoView({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [scrollToSection, leadData]);

  const handleRefreshLead = async () => {
    await fetchLeadData();  // this already reloads the lead API
  };


  const handleSave = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsEditing(false);
    setLoading(false);
  };
  // Standardized stage change confirmation
  const stageChangeConfirm = useConfirmModal({
    type: "stage-change",
    onConfirm: async (stageData) => {
      const result = await bulkUpdateLeadStage({
        leadIds: [leadData.id],
        stageId: stageData.stageId,
        subStageId: stageData.subStageId,
        remarks: stageData.notes || "",
        leadScore: stageData.leadScore,
      });

      if (result.success) {
        // Refetch lead data
        const refreshResult = await getLead(leadData.id);
        if (refreshResult.success && refreshResult.data.data) {
          const transformedData = transformLeadDetailsApiData(
            refreshResult.data.data,
            leadData.id,
          );
          setSelectedStage(transformedData.stage);
          setSelectedSubStage(transformedData.subStage);

          setLeadData(transformedData);
        }

        // Notify parent about the update
        if (onLeadUpdate) {
          onLeadUpdate(leadData.id, 'update');
        }
      }
      return result;
    },
    messages: {
      pending: {
        title: "Change Lead Stage?",
        description: `Are you sure you want to change the stage for ${leadData?.name || "this lead"}? This action will update the lead progression.`,
        actionText: "Change Stage",
      },
      success: {
        title: "Stage Changed Successfully",
        description: `Successfully updated the stage for ${leadData?.name || "the lead"}. The changes have been saved.`,
      },
    },
  });

  const handleStageChange = async (stageData, resetFormData = null) => {
    // Check if stageData is valid
    if (!stageData?.leadId || !stageData.stageId) {
      console.error("Missing required stage change data");
      toast.error("Error: Stage ID is missing. Please select a valid stage.");
      return;
    }

    try {
      setStageChangeData(stageData);

      // Call the form reset function if provided (from modal/panel)
      if (resetFormData && typeof resetFormData === "function") {
        resetFormData();
      }

      // Close any open modals/panels
      setShowStageModal(false);

      // Open confirmation modal
      stageChangeConfirm.openModal(stageData);
    } catch (error) {
      console.error("Error in handleStageChange:", error);
      toast.error("An error occurred while processing the stage change.");
    }
  };

  // Handle follow-up scheduling
  const handleFollowUpScheduled = async (followUpData) => {
    const isEditing = editingFollowUp !== null;

    try {
      let result;
      if (isEditing && editingFollowUp) {
        // Update existing follow-up
        result = await updateFollowUp(editingFollowUp.id, followUpData);
      } else {
        // Create new follow-up
        result = await createFollowUp(followUpData);
      }

      setShowFollowUpModal(false);
      setEditingFollowUp(null);

      // Refresh lead data to update upcoming follow-ups
      await fetchLeadData();

      return true;
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'scheduling'} follow-up:`, error);
      // Error toast is shown in the service function
      return false;
    }
  };

  const handleEditFollowUp = (followUp) => {
    // Transform the follow-up data to the format expected by the modal
    const editData = {
      id: followUp.FollowUpId || followUp.id,
      leadId: leadData?.id || followUp.LeadId || followUp.leadId,
      userId: followUp.UserId || followUp.userId,
      userName: followUp.LeadName || followUp.userName || leadData?.name,
      status: followUp.StatusName || followUp.status,
      statusId: followUp.StatusId || followUp.statusId,
      outcomeId: followUp.OutcomeId || followUp.outcomeId,
      remarks: followUp.Remarks || followUp.remarks,
      notes: followUp.Notes || followUp.notes, // Missing field
      Notes: followUp.Notes || followUp.notes, // Missing field (PascalCase)
      scheduledTime: followUp.scheduledTime || followUp.ScheduledTime,
      date: followUp.ScheduledDate || followUp.date,
      priority: followUp.Priority || followUp.priority,
      // Use AssignedToId (GUID) instead of AssignedTo (name)
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName,
      channelId: followUp.ChannelId || followUp.channelId,
      type: followUp.type
    };

    setEditingFollowUp(editData);
    setShowFollowUpModal(true);
  };

  const handleUpdateFollowUpStatus = (followUp) => {
    // Check if status is already final
    if (isStatusFinal(followUp)) {
      toast.error('Cannot update status', {
        description: `This follow-up is already marked as ${followUp.StatusName || followUp.status}`,
        duration: 3000,
      });
      return;
    }

    // Set the selected follow-up with proper field mapping
    const followUpData = {
      ...followUp,
      // Map AssignedToId to assignedTo for consistency
      assignedTo: followUp.AssignedToId || followUp.assignedToId || followUp.AssignedTo || followUp.assignedTo,
      assignedToName: followUp.AssignedTo || followUp.AssignedToName || followUp.assignedToName
    };

    setSelectedFollowUpForStatus(followUpData);

    // Match the existing status by name to get the correct local statusOption id
    const currentStatusName = followUp.StatusName || followUp.status || '';
    const matchedStatus = statusOptions.find(
      s => s.name.toLowerCase() === currentStatusName.toLowerCase()
    );

    // Pre-fill the status form
    setStatusFormData({
      status: matchedStatus?.name || currentStatusName || '',
      statusId: matchedStatus?.id || null,
      outcome: followUp.EventName || followUp.eventName || '',
      outcomeId: followUp.OutcomeId || followUp.outcomeId || null,
      eventTypeId: followUp.EventTypeId || followUp.eventTypeId || followUp.OutcomeId || followUp.outcomeId || null,
      remarks: followUp.Remarks || followUp.remarks || '',
      Notes: followUp.Notes || followUp.notes,
      notes: followUp.Notes || followUp.notes
    });

    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {

    // Validate required fields
    if (!statusFormData.status || !statusFormData.statusId) {
      toast.error('Please select a status');
      return;
    }
    if (!selectedFollowUpForStatus) {
      toast.error('No follow-up selected');
      return;
    }

    try {
      // Prepare status data for individual update (use ID not name)
      const statusData = {
        status: statusFormData.status,
        statusId: statusFormData.statusId,
        outcomeId: statusFormData.outcomeId || 0,
        eventTypeId: statusFormData.eventTypeId,
        remarks: statusFormData.remarks || "",
        // Use AssignedToId (the GUID) instead of AssignedTo (the name)
        assignedTo: selectedFollowUpForStatus.assignedTo || selectedFollowUpForStatus.AssignedToId || selectedFollowUpForStatus.assignedToId,
        priority: selectedFollowUpForStatus.Priority || selectedFollowUpForStatus.priority || "medium",
        newScheduledDate: selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date
          ? new Date(selectedFollowUpForStatus.ScheduledDate || selectedFollowUpForStatus.date).toISOString()
          : new Date().toISOString(),
        newScheduledTime: selectedFollowUpForStatus.ScheduledTime || selectedFollowUpForStatus.scheduledTime || "09:00"
      };


      // Call the API to update the status
      await updateFollowUpStatus(selectedFollowUpForStatus.FollowUpId || selectedFollowUpForStatus.id, statusData);

      // Success toast is shown in the service function
      setShowStatusModal(false);
      setSelectedFollowUpForStatus(null);

      // Refresh lead data to update upcoming follow-ups
      await fetchLeadData();
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      // Error toast is shown in the service function
    }
  };

  const handleAddFollowUp = () => {
    setEditingFollowUp(null);
    setShowFollowUpModal(true);
  };
  // Refresh only progress journey data
  const refreshProgressJourney = async () => {
    try {
      const response = await getLeadJourneyIndicators(leadId);
      if (response.success && response.data) {
        setProgressData(response.data);
      } else {
        console.error('Failed to refresh progress journey:', response.error);
      }
    } catch (err) {
      console.error('Error refreshing progress journey:', err);
    }
  };
  const handlePriorityChange = async (priority) => {
    try {
      setShowPriorityModal(false);
      toast.success("Priority updated successfully");
      await fetchLeadData();
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority. Please try again.");
    }
  };
  const handleInteractionLevelChange = async (interactionLevel) => {
    try {
      setShowInteractionLevelModal(false);
      toast.success("Interaction Level updated successfully");
      await fetchLeadData();
    } catch (error) {
      console.error("Error updating interaction level:", error);
      toast.error("Failed to update interaction level. Please try again.");
    }
  };
  const handleStepClick = (stepId) => {
    // Don't allow clicking on offer/acceptance letter steps if it's filtered out for Indian leads
    if (leadData?.country?.toLowerCase() === 'india' && (stepId === 3 || stepId === 11)) {
      return;
    }

    switch (stepId) {
      case 1:
        // Application Details -> Lead Details sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("lead-details");
        break;
      case 2:
        // Document Upload -> Documents tab (reset content tab)
        setSelectedTab("documents");
        setSelectedContentTab("lead-details"); // Reset to default
        break;
      case 3:
        // Offer Letter Generated -> Navigate to offer letter tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("offer-letter");
        break;
      case 11:
        // Acceptance Letter Generated -> Navigate to acceptance letter tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("acceptance-letter");
        break;
      case 4:
        // Payment Link -> Payment Details sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("payment-details");
        break;
      case 5:
        // Payment Successful -> Payment Details sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("payment-details");
        break;
      case 6:
        // Application Initiated -> Application Form sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("application-form");
        break;
      case 7:
        // Application Submitted -> Application Form sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("application-form");
        break;
      case 8:
        // Interview Scheduled -> Google Meet sub tab
        setSelectedTab("leads-tab");
        setSelectedContentTab("google-meet");
        break;
      default:
        setSelectedTab("leads-tab");
        setSelectedContentTab("lead-details");
    }


    // Auto-scroll to content section
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };
  const renderTabContent = () => {
    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "lead-details"
    ) {
      return (
        <div id="section-leads-tab">
          <LeadDetailsForm
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            loading={loading}
            leadData={leadData}
            handleSave={handleSave}
            onOpenQuickLeadModal={() => setShowQuickLeadModal(true)}
            onOpenNotesModal={() => {
              setSelectedTab('notes');
              setInternalOpenNotesAddModal(true); // ✅ always works
              if (setOpenNotesAddModal) setOpenNotesAddModal(true); // sync prop if provided
            }}
            leadId={leadId}
          />

          <QuickLeadModal
            isOpen={showQuickLeadModal}
            onClose={() => setShowQuickLeadModal(false)}
            leadId={leadId}
            categories={leadData?.LeadFieldCategories || []}
            onLeadUpdated={fetchLeadData}
          />
        </div>
      );
    }

    if (selectedTab === "timeline") {
      return <div id="section-timeline"><TimelineView leadId={leadId} entityName={finalEntityName} /></div>;
    }

    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "payment-details"
    ) {
      return <div id="section-payment-details"><PaymentDetailsTab leadData={leadData} isInternational={leadData?.IsInternationalLead} isMobile={isMobile} progressData={progressData} refreshProgressJourney={refreshProgressJourney} refreshLeadData={fetchLeadData} leadId={leadId} /></div>;
    }



    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "follow-up-calendar"
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const dateParam = searchParams.get('date');
      return (
        <div id="section-follow-up-calendar">
          <CalendarTab
            isMobile={isMobile}
            leadData={leadData}
            initialDate={dateParam}
            isStudent={leadData?.IsStudent}
          />
        </div>
      );
    }

    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "application-form"
    ) {
      // Show application form if ApplicationId is available in progressData
      return <div id="section-application-form"><ApplicationFormViewTab leadUserId={leadData?.LeadUserId} refreshProgressJourney={refreshProgressJourney} onLeadDataRefresh={fetchLeadData} applicationId={progressData.ApplicationId} progressData={progressData} isMobile={isMobile} leadData={leadData} /></div>;
    }

    if (selectedTab === "notes") {
      return (
        <div id="section-notes">
          <NotesTab
            isMobile={isMobile}
            leadData={leadData}
            openAddModalExternal={openNotesAddModal || internalOpenNotesAddModal}
            setOpenAddModalExternal={(val) => {
              setInternalOpenNotesAddModal(val);
              if (setOpenNotesAddModal) setOpenNotesAddModal(val);
            }}
          />
        </div>
      );
    }

    if (selectedTab === "calendar") {
      return <div id="section-calendar"><CalendarTab isMobile={isMobile} leadData={leadData} isStudent={leadData?.IsStudent} /></div>;
    }

    if (selectedTab === "communications") {
      return <div id="section-communications"><CommunicationsTab leadId={leadId} isMobile={isMobile} initialChannelId={initialSubTab} /></div>;
    }

    if (selectedTab === "documents") {
      return <div id="section-documents"><DocumentsTab IsFinalApproved={progressData?.IsFinalApproved} isMobile={isMobile} leadId={leadId} refreshProgressJourney={refreshProgressJourney} isStudent={leadData?.IsStudent} /></div>;
    }

    if (selectedTab === "tickets") {
      return <div id="section-tickets"><TicketsTab isMobile={isMobile} leadId={leadId} isStudent={leadData?.IsStudent} /></div>;
    }

    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "google-meet"
    ) {
      return <div id="section-google-meet"><GoogleMeetTab leadData={leadData} isMobile={isMobile} /></div>;
    }

    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "offer-letter"
    ) {
      return <div id="section-offer-letter"><OfferLetterTab leadId={leadId} leadData={leadData} progressData={progressData} isMobile={isMobile} onOfferLetterGenerated={fetchLeadData} /></div>;
    }

    if (
      selectedTab === "leads-tab" &&
      selectedContentTab === "acceptance-letter"
    ) {
      return <div id="section-acceptance-letter"><AcceptanceLetterTab leadId={leadId} leadData={leadData} progressData={progressData} isMobile={isMobile} /></div>;
    }

    return null;
  };
  // Permission check (skip in modal mode - parent handles permissions)
  if (!isModal && !hasPermission("MLLV")) {
    return (
      <Error401 />
    )
  }

  // Don't render content until data is loaded (UI handled by global loader)
  if (loading || !leadData) {
    if (isModal) {
      return (
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <div className="border-primary-600 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
            <p className="dark:text-dark-400 text-gray-500">Loading lead details...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // Show error state
  if (error && !leadData) {
    const errorContent = (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h3 className="dark:text-dark-100 mb-2 text-lg font-medium text-gray-900">
            Error Loading Lead
          </h3>
          <p className="dark:text-dark-400 mb-4 text-gray-500">{error}</p>
          <Button
            onClick={() => isModal ? fetchLeadData() : window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Retry
          </Button>
        </div>
      </div>
    );

    if (isModal) {
      return errorContent;
    }

    return (
      <Page
        title={`${formatLeadName(leadData)} Details`}
        component="div"
        className="dark:bg-dark-800 min-h-screen bg-gray-50"
      >
        {errorContent}
      </Page>
    );
  }

  // Main content - used in both modal and page mode
  const mainContent = (
    <>
      {/* Error banner if API failed but fallback data is shown */}
      {error && leadData && (
        <div className="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:bg-yellow-900/20">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                <strong>Warning:</strong> Using fallback data. API Error:{" "}
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex flex-col md:flex-row ${isModal ? "h-full" : isMobile ? "min-h-screen" : ""} ${isMobile ? "mobile-content" : isModal ? "" : ""}`}
      >
        {/* Main Content Area */}
        <div
          className={`flex flex-1 flex-col ${isMobile ? "mobile-flex-fix" : "min-w-0"}`}
        >
          <div
            className={`${isMobile ? "mobile-content w-full max-w-full px-2 py-4" : isModal ? "container mx-auto px-4 py-2" : "container mx-auto px-4 py-6"}`}
          >
            <LeadDetailsHeader
              isFavorite={isFavorite}
              setIsFavorite={setIsFavorite}
              isFlaged={isFlaged}
              setIsFlaged={setIsFlaged}
              showRightPanel={showRightPanel}
              setShowRightPanel={setShowRightPanel}
              LeadRightSidebar={LeadRightSidebar}
              handleStageChange={handleStageChange}
              leadData={leadData}
              setIsFollowUpOpen={setIsFollowUpOpen}
              isMobile={isMobile}
              stageConfirmModal={stageConfirmModal}
              showMobileSidebar={showMobileSidebar}
              setShowMobileSidebar={setShowMobileSidebar}
              onLeadDataRefresh={fetchLeadData}
              showStageModal={showStageModal}
              setShowStageModal={setShowStageModal}
              // Pass modal setters to LeadRightSidebar
              setShowOwnerModal={setShowOwnerModal}
              setShowPriorityModal={setShowPriorityModal}
              setShowAnalyticsModal={setShowAnalyticsModal}
              setShowWhatsAppModal={setShowWhatsAppModal}
              setShowEmailModal={setShowEmailModal}
              setShowSMSModal={setShowSMSModal}
              setShowFollowUpModal={setShowFollowUpModal}
              setShowCallModal={setShowCallModal}
              setShowInteractionLevelModal={setShowInteractionLevelModal}
              // Quick Actions handlers for header
              onWhatsApp={() => setShowWhatsAppModal(true)}
              onCall={() => setShowCallModal(true)}
              onEmail={() => setShowEmailModal(true)}
              onFollowUp={() => setShowFollowUpModal(true)}
              onSMS={() => setShowSMSModal(true)}
              // Lead Management handlers for header
              onChangeStage={() => setShowStageModal(true)}
              onSetPriority={() => setShowPriorityModal(true)}
              onSetInteractionLevel={() => setShowInteractionLevelModal(true)}
              entityName={finalEntityName}
              isModal={isModal}
              progressData={progressData}
            />

            {/* Enhanced Header Section */}
            <Card
              className={`mb-4 p-3 sm:mb-6 sm:p-6 ${isMobile ? "mobile-content" : ""}`}
            >
              {/* Lead Basic Info */}
              <LeadBasicInfo
                leadData={leadData}
                isFavorite={isFavorite}
                isFlaged={isFlaged}
                selectedStage={selectedStage}
                selectedSubStage={selectedSubStage}
                isMobile={isMobile}
                onEditStage={() => setShowStageModal(true)}
                onEditPriority={() => setShowPriorityModal(true)}
                onEditInteractionLevel={() => setShowInteractionLevelModal(true)}
                // Lead Management handlers
                onChangeStage={() => setShowStageModal(true)}
                onSetPriority={() => setShowPriorityModal(true)}
                onSetInteractionLevel={() => setShowInteractionLevelModal(true)}
                onRefreshLead={handleRefreshLead}
                onOpenNotesModal={() => {
                  setSelectedTab('notes');
                  setInternalOpenNotesAddModal(true); // ✅ always works
                  if (setOpenNotesAddModal) setOpenNotesAddModal(true); // sync prop if provided
                }}
                onEmailClick={() => setShowEmailModal(true)}
                entityName={finalEntityName}
              />

              {/* Stats Cards with Lead Management and Quick Actions */}
              <StatusCards
                isMobile={isMobile}
                leadData={leadData}
                onAssignOwner={() => setShowOwnerModal(true)}
                onEditFollowUp={handleEditFollowUp}
                onUpdateStatus={handleUpdateFollowUpStatus}
                onAddFollowUp={handleAddFollowUp}
                // Quick Actions handlers
                onWhatsApp={() => setShowWhatsAppModal(true)}
                onCall={() => setShowCallModal(true)}
                onEmail={() => setShowEmailModal(true)}
                onFollowUp={() => setShowFollowUpModal(true)}
                onSMS={() => setShowSMSModal(true)}
                // Lead Management handlers
                onChangeStage={() => setShowStageModal(true)}
                onSetPriority={() => setShowPriorityModal(true)}
                onSetInteractionLevel={() => setShowInteractionLevelModal(true)}
                onAssignAcademic={() => setShowAcademicModal(true)}
                onAssignAccountant={() => setShowAccountantModal(true)}
              />

              <ProgressStepper 
                isMobile={isMobile} 
                progressData={progressData} 
                handleStepClick={handleStepClick} 
                setProgressData={setProgressData} 
                leadId={leadId} 
                leadCountry={leadData?.country} 
                isLeadView={finalEntityName === "Lead"}
              />
            </Card>

            {/* Main Content */}
            <Card
              ref={contentRef}
              className={`${isMobile ? "mobile-content overflow-visible" : "overflow-hidden"}`}
            >
              <div
                className={`flex ${isMobile ? "mobile-flex-fix flex-col" : ""} ${isMobile ? "min-h-0" : ""}`}
              >
                <SidebarNavigation
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                  selectedContentTab={selectedContentTab}
                  setSelectedContentTab={setSelectedContentTab}
                  isMobile={isMobile}
                  showMobileSidebar={showMobileSidebar}
                  setShowMobileSidebar={setShowMobileSidebar}
                  entityName={finalEntityName}
                  leadData={leadData}
                />

                {/* Content Area */}
                <div
                  className={`flex-1 ${isMobile ? "mobile-flex-fix" : "min-w-0"}`}
                >
                  {/* Content */}
                  <div
                    className={`p-3 sm:p-6 ${isMobile ? "mobile-content" : ""}`}
                  >
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Stage Change Confirmation Modal */}
        <ConfirmModal
          show={stageChangeConfirm.modalState.show}
          state={stageChangeConfirm.modalState.state}
          confirmLoading={stageChangeConfirm.modalState.loading}
          onClose={stageChangeConfirm.closeModal}
          onOk={stageChangeConfirm.handleConfirm}
          messages={stageChangeConfirm.messages}
          buttonColor="primary"
        />
        {/* Modals */}
        <StageChangeModal
          isOpen={showStageModal}
          onClose={() => setShowStageModal(false)}
          leadData={leadData}
          onStageChange={handleStageChange}
        />

        {/* Action Modals */}
        <AssignOwnerModal
          isOpen={showOwnerModal}
          onClose={() => setShowOwnerModal(false)}
          leadData={leadData}
          currentOwnerName={leadData?.assignedOwner !== "N/A" ? leadData?.assignedOwner : null}
          onOwnerChange={fetchLeadData}
        />

        <SetPriorityModal
          isOpen={showPriorityModal}
          onClose={() => setShowPriorityModal(false)}
          leadData={leadData}
          handlePriorityChange={handlePriorityChange}
        />
        <SetInteractionLevelModal
          isOpen={showInteractionLevelModal}
          onClose={() => setShowInteractionLevelModal(false)}
          leadData={leadData}
          handleInteractionLevelChange={handleInteractionLevelChange}
        />

        <LeadAnalyticsModal
          isOpen={showAnalyticsModal}
          onClose={() => setShowAnalyticsModal(false)}
          leadData={leadData}
        />

        <WhatsAppMessageModal
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          leadData={leadData}
        />

        <EmailMessageModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          leadData={leadData}
        />

        <SMSMessageModal
          isOpen={showSMSModal}
          onClose={() => setShowSMSModal(false)}
          leadData={leadData}
        />

        <FollowUpScheduleModal
          key={editingFollowUp?.id || editingFollowUp?.FollowUpId || 'new-follow-up'}
          isOpen={showFollowUpModal}
          onClose={() => {
            setShowFollowUpModal(false);
            setEditingFollowUp(null);
          }}
          leadData={leadData}
          editingFollowUp={editingFollowUp}
          onScheduleFollowUp={handleFollowUpScheduled}
        />

        {/* Status Update Modal - Same as FollowUpsCalendar */}
        <Transition appear show={showStatusModal} as={Fragment}>
          <Dialog as="div" className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClose={() => { }}>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
            </TransitionChild>

            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-2xl max-h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all dark:bg-dark-800 flex flex-col border border-gray-200 dark:border-dark-600">
                {/* Modern Header */}
                <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                        <PencilIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <DialogTitle as="h3" className="text-xl font-semibold text-gray-900 dark:text-white">
                          Update Follow-up Status
                        </DialogTitle>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Change the status and outcome of this follow-up
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowStatusModal(false)}
                      variant="flat"
                      isIcon
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
                  {/* Current Follow-up Info */}
                  {selectedFollowUpForStatus && (
                    <Card className="p-4 border border-gray-200 dark:border-dark-600">
                      <div className="flex items-start space-x-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-dark-700 rounded-full">
                          <UserIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {leadData?.name || 'Lead'}
                          </h4>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                            {(selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName) && (
                              <div className="flex items-center space-x-1">
                                <UserIcon className="w-4 h-4" />
                                <span>{selectedFollowUpForStatus?.assignedToName || selectedFollowUpForStatus?.AssignedTo || selectedFollowUpForStatus?.UserName}</span>
                              </div>
                            )}
                            {(selectedFollowUpForStatus?.scheduledTime || selectedFollowUpForStatus?.ScheduledTime) && (
                              <div className="flex items-center space-x-1">
                                <ClockIcon className="w-4 h-4" />
                                <span>{selectedFollowUpForStatus?.scheduledTime || selectedFollowUpForStatus?.ScheduledTime}</span>
                              </div>
                            )}
                            {selectedFollowUpForStatus?.StatusName && (
                              <Badge variant="soft" color="neutral">
                                {selectedFollowUpForStatus.StatusName}
                              </Badge>
                            )}
                          </div>

                          {/* Previous Remarks */}
                          {selectedFollowUpForStatus?.Remarks && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                              <div className="flex items-center space-x-2 mb-2">
                                <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-gray-500" />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Previous Remarks</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                {selectedFollowUpForStatus.Remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                    {/* Status Selection */}
                    <div className="lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                        <div className="flex items-center space-x-2">
                          <CheckIcon className="w-4 h-4 text-primary-600" />
                          <span>Status</span>
                          <span className="text-red-500">*</span>
                        </div>
                      </label>
                      <Select
                        value={statusFormData.statusId || ''}
                        onChange={(e) => {
                          const selectedStatus = statusOptions.find(s => s.id === parseInt(e.target.value));
                          setStatusFormData(prev => ({
                            ...prev,
                            status: selectedStatus?.name || '',
                            statusId: selectedStatus?.id || null
                          }));
                        }}
                        className="w-full"
                        placeholder="Choose status..."
                      >
                        <option value="">Choose status...</option>
                        {statusOptions.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </Select>
                    </div>

                    {/* Outcome Selection */}
                    {/* <div className="lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                        <div className="flex items-center space-x-2">
                          <FlagIcon className="w-4 h-4 text-primary-600" />
                          <span>Outcome Event</span>
                          <span className="text-red-500">*</span>
                        </div>
                      </label>
                      {loadingEvents ? (
                        <div className="w-full h-10 border border-gray-300 dark:border-dark-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-dark-700">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Loading events...</span>
                          </div>
                        </div>
                      ) : (
                        <Select
                          value={statusFormData.eventTypeId || ''}
                          onChange={(e) => {
                            const selectedEvent = followUpEvents.find(event => event.id === e.target.value);
                            setStatusFormData(prev => ({ 
                              ...prev, 
                              eventTypeId: selectedEvent?.id || null,
                              outcome: selectedEvent?.name || '',
                              outcomeId: selectedEvent?.id || null
                            }));
                          }}
                          className="w-full"
                          placeholder="Choose outcome..."
                        >
                          <option value="">Choose outcome...</option>
                          {followUpEvents.map((event) => (
                            <option key={event.id} value={event.id}>
                              {event.name} {event.defaultScore !== undefined ? `(${event.defaultScore > 0 ? '+' : ''}${event.defaultScore})` : ''}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div> */}
                  </div>

                  {/* Selected Event Info */}
                  {/* {statusFormData.eventTypeId && (
                    <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700">
                      {(() => {
                        const selectedEvent = followUpEvents.find(e => e.id === statusFormData.eventTypeId);
                        return selectedEvent ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                                <FlagIcon className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <div className="font-medium text-green-900 dark:text-green-100">{selectedEvent.name}</div>
                                {selectedEvent.description && (
                                  <div className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                                    {selectedEvent.description}
                                  </div>
                                )}
                              </div>
                            </div>
                            {selectedEvent.defaultScore !== undefined && (
                              <Badge 
                                variant="solid" 
                                color={selectedEvent.defaultScore > 0 ? "success" : selectedEvent.defaultScore < 0 ? "error" : "neutral"}
                                className="text-sm font-medium"
                              >
                                {selectedEvent.defaultScore > 0 ? '+' : ''}{selectedEvent.defaultScore} points
                              </Badge>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </Card>
                  )} */}

                  {/* Remarks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                      <div className="flex items-center space-x-2">
                        <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-primary-600" />
                        <span>Remarks</span>
                        <span className="text-gray-400 text-xs">(Optional)</span>
                      </div>
                    </label>
                    <Textarea
                      value={statusFormData.remarks}
                      onChange={(e) => setStatusFormData(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Add notes about this follow-up outcome..."
                      rows={4}
                      className="w-full resize-none"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-750">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Required fields marked with *</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        onClick={() => {
                          setShowStatusModal(false);
                          setStatusFormData({ status: '', statusId: null, outcome: '', outcomeId: null, eventTypeId: null, remarks: '' });
                        }}
                        variant="outlined"
                        color="neutral"
                        size="md"
                        className="min-w-[100px]"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleStatusUpdate}
                        variant="solid"
                        color="primary"
                        size="md"
                        disabled={!statusFormData.status}
                        className="min-w-[120px] shadow-lg"
                      >
                        <CheckIcon className="w-4 h-4 mr-2" />
                        Update Status
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </Dialog>
        </Transition>

        <CallModal
          isOpen={showCallModal}
          onClose={() => {
            setShowCallModal(false);
            // Note: The CallModal itself handles global widget state management
            // in its useEffect hooks, so no additional state management needed here
          }}
          leadData={leadData}
          onCallComplete={(callData) => {
            // You can add logic here to refresh call history or other components
            setShowCallModal(false);
          }}
        />

        <AssignAcademicCounsellorModal
          isOpen={showAcademicModal}
          onClose={() => setShowAcademicModal(false)}
          leadData={leadData}
          currentAcademicCounsellorName={
            leadData?.SourceDetails?.LeadAcademicCounsellorName ||
            leadData?.LeadAcademicCounsellorName ||
            leadData?.academicCounsellorName
          }
          onAcademicCounsellorChange={fetchLeadData}
        />

        <AssignAccountantCounsellorModal
          isOpen={showAccountantModal}
          onClose={() => setShowAccountantModal(false)}
          leadData={leadData}
          currentAccountantCounsellorName={
            leadData?.SourceDetails?.LeadAccountantCounsellorName ||
            leadData?.accountsCounsellorName ||
            leadData?.accountsCounsellor
          }
          onAccountantCounsellorChange={fetchLeadData}
        />
      </div>
    </>
  );

  // In modal mode, return content directly without Page wrapper
  if (isModal) {
    return mainContent;
  }

  // In page mode, wrap with Page component
  return (
    <Page
      title={`${formatLeadName(leadData)}${leadData?.FatherName ? ` ${leadData?.gender?.trim()?.toLowerCase() === 'male' ? 'S/O' : leadData?.gender?.trim()?.toLowerCase() === 'female' ? 'D/O' : 'C/O'} ${leadData.FatherName}` : ""} Details`}
      component="div"
      className={`dark:bg-dark-800 min-h-screen bg-gray-50 ${isMobile ? "mobile-container" : ""}`}
    >
      {mainContent}
    </Page>
  );
};

export default LeadDetails;
